const sumMap = (map) => Object.values(map || {})
  .reduce((acc, value) => acc + (Number(value) || 0), 0);

const countUnreadItems = (items) => Object.values(items || {}).reduce((total, item) => {
  if (!item || typeof item !== 'object') return total;
  return total + (item.cnt !== undefined ? Number(item.cnt) || 0 : countUnreadItems(item));
}, 0);

const buildLiveProductMessageLinks = (prevData, nextData, prevLinks) => {
  const nextLinks = {};

  ['buyer', 'seller'].forEach((role) => {
    const prevBlock = (prevData && prevData[role]) || {};
    const nextBlock = (nextData && nextData[role]) || {};
    const prevMsgs = prevBlock.product_messages || {};
    const nextMsgs = nextBlock.product_messages || {};
    const links = {};

    Object.entries((prevLinks && prevLinks[role]) || {}).forEach(([msgId, productId]) => {
      if ((Number(nextMsgs[msgId]) || 0) > 0) links[msgId] = productId;
    });

    if (Object.keys(prevBlock).length) {
      const prevProducts = prevBlock.products || {};
      const nextProducts = nextBlock.products || {};

      const newMsgIds = Object.keys(nextMsgs).filter((msgId) => (
        (Number(nextMsgs[msgId]) || 0) > 0
        && !(Number(prevMsgs[msgId]) || 0)
        && links[msgId] === undefined
      ));
      const grownProducts = Object.keys(nextProducts).filter((productId) => (
        (Number(nextProducts[productId]) || 0) > (Number(prevProducts[productId]) || 0)
      ));

      if (newMsgIds.length && grownProducts.length === 1) {
        const [grownProductId] = grownProducts;
        newMsgIds.forEach((msgId) => { links[msgId] = grownProductId; });
      }
    }

    nextLinks[role] = links;
  });

  return nextLinks;
};

export default {
  state: {
    unreadMessages: 0,
    unreadProjects: [],
    unreadGroups: [],
    unreadMembers: [],
    unreadFeeds: [],
    unreadBuyerMarketplace: [],
    unreadSellerMarketplace: [],
    unreadMarketplaceData: {},
    liveProductMessageLinks: {},
  },
  getters: {
    getUnreadCounts: (state) => ({
      messages: state.unreadMessages,
      feeds: state.unreadFeeds.length,
      friends: state.unreadMembers.length,
      groups: countUnreadItems(state.unreadGroups),
      projects: countUnreadItems(state.unreadProjects),
    }),
    getUnreadProjects: (state) => state.unreadProjects,
    getUnreadGroups: (state) => state.unreadGroups,
    getUnreadMembers: (state) => state.unreadMembers,
    getUnreadFeeds: (state) => state.unreadFeeds,
    getUnreadBuyerMarketplace: (state) => state.unreadBuyerMarketplace,
    getUnreadSellerMarketplace: (state) => state.unreadSellerMarketplace,
    getUnreadMarketplaceData: (state) => state.unreadMarketplaceData,

    marketplaceBlock: (state) => (role) => (
      (state.unreadMarketplaceData && state.unreadMarketplaceData[role]) || {}
    ),
    marketplaceBulb: (state, getters) => (role, marketplaceId) => (
      Number((getters.marketplaceBlock(role).marketplaces || {})[marketplaceId]) || 0
    ),
    marketplaceRoleTotal: (state, getters) => (role) => {
      const block = getters.marketplaceBlock(role);
      const productTotal = sumMap(block.marketplaces);
      const rfqTotal = role === 'buyer'
        ? sumMap(block.projects)
        : sumMap(block.messages);

      return productTotal + rfqTotal;
    },
    marketplaceProductsTotal: (state, getters) => (role) => (
      sumMap(getters.marketplaceBlock(role).marketplaces)
    ),
    productBulb: (state, getters) => (role, productId) => (
      Number((getters.marketplaceBlock(role).products || {})[productId]) || 0
    ),
    productOfferBulb: (state, getters) => (role, offerId) => {
      const block = getters.marketplaceBlock(role);
      const newOffer = role === 'seller' ? (Number((block.offers || {})[offerId]) || 0) : 0;
      const offerMessages = Number((block.product_offers || {})[offerId]) || 0;
      return newOffer + offerMessages;
    },
    productOfferThreadBulb: (state, getters) => (role, offerId) => (
      Number((getters.marketplaceBlock(role).product_offers || {})[offerId]) || 0
    ),
    productMessageBulb: (state, getters) => (role, msgIds) => {
      const map = getters.marketplaceBlock(role).product_messages || {};
      return (Array.isArray(msgIds) ? msgIds : []).reduce(
        (acc, id) => acc + (Number(map[id]) || 0), 0,
      );
    },
    productLiveMessageBulb: (state, getters) => (role, productId) => {
      const links = (state.liveProductMessageLinks || {})[role] || {};
      const map = getters.marketplaceBlock(role).product_messages || {};
      return Object.keys(links).reduce((acc, msgId) => (
        String(links[msgId]) === String(productId) ? acc + (Number(map[msgId]) || 0) : acc
      ), 0);
    },
  },
  mutations: {
    SET_UNREAD_MESSAGES(state, payload) {
      state.unreadMessages = Number(payload) || 0;
    },
    SET_UNREAD_PROJECTS(state, payload) {
      state.unreadProjects = payload;
    },
    SET_UNREAD_GROUPS(state, payload) {
      state.unreadGroups = payload;
    },
    SET_UNREAD_MEMBERS(state, payload) {
      state.unreadMembers = payload;
    },
    SET_UNREAD_FEEDS(state, payload) {
      state.unreadFeeds = payload;
    },
    SET_UNREAD_BUYER_MARKETPLACE(state, payload) {
      state.unreadBuyerMarketplace = payload;
    },
    SET_UNREAD_SELLER_MARKETPLACE(state, payload) {
      state.unreadSellerMarketplace = payload;
    },
    SET_UNREAD_MARKETPLACE_DATA(state, payload) {
      const data = payload && typeof payload === 'object' ? payload : {};
      state.liveProductMessageLinks = buildLiveProductMessageLinks(
        state.unreadMarketplaceData,
        data,
        state.liveProductMessageLinks,
      );
      state.unreadMarketplaceData = data;
      const buyerTotal = this.getters.marketplaceRoleTotal('buyer');
      const sellerTotal = this.getters.marketplaceRoleTotal('seller');
      if (data.buyer) data.buyer.total = buyerTotal;
      if (data.seller) data.seller.total = sellerTotal;
      this.commit('SET_UNREAD_BUYER_MARKETPLACE', buyerTotal);
      this.commit('SET_UNREAD_SELLER_MARKETPLACE', sellerTotal);
    },
    CLEAR_MARKETPLACE_BULB(state, payload) {
      const data = state.unreadMarketplaceData;
      const role = payload && payload.role;
      const block = data && role ? data[role] : null;
      if (!block) return;

      const {
        kind, marketplaceId, productId, offerId,
      } = payload;
      const msgIds = Array.isArray(payload.msgIds) ? payload.msgIds : [];

      const take = (field, key) => {
        const map = block[field];
        if (!map || key === undefined || key === null) return 0;
        const amount = Number(map[key]) || 0;
        if (amount) map[key] = 0;
        return amount;
      };
      const decrement = (field, key, amount) => {
        if (!amount) return;
        const map = block[field];
        if (!map || key === undefined || key === null || map[key] === undefined) return;
        const next = (Number(map[key]) || 0) - amount;
        map[key] = next > 0 ? next : 0;
      };

      let removed = 0;

      if (kind === 'new-offer') {
        removed += take('offers', offerId);
      } else if (kind === 'offer-messages') {
        removed += take('product_offers', offerId);
        msgIds.forEach((id) => { take('product_offer_messages', id); });
      } else if (kind === 'product-messages') {
        msgIds.forEach((id) => { removed += take('product_messages', id); });

        const links = { ...((state.liveProductMessageLinks || {})[role] || {}) };
        let linksChanged = false;
        msgIds.forEach((id) => {
          if (links[id] !== undefined) {
            delete links[id];
            linksChanged = true;
          }
        });
        if (productId !== undefined && productId !== null) {
          Object.keys(links).forEach((msgId) => {
            if (String(links[msgId]) === String(productId)) {
              removed += take('product_messages', msgId);
              delete links[msgId];
              linksChanged = true;
            }
          });
        }
        if (linksChanged) {
          state.liveProductMessageLinks = {
            ...state.liveProductMessageLinks,
            [role]: links,
          };
        }
      }

      decrement('products', productId, removed);
      decrement('marketplaces', marketplaceId, removed);

      const roleTotal = this.getters.marketplaceRoleTotal(role);
      block.total = roleTotal;
      this.commit(
        role === 'seller' ? 'SET_UNREAD_SELLER_MARKETPLACE' : 'SET_UNREAD_BUYER_MARKETPLACE',
        roleTotal,
      );

      state.unreadMarketplaceData = { ...data };
    },
  },
};
